/**
 * Membresia: os números da comunidade calculados dos próprios cadastros e eventos — sem
 * dado ilustrativo. Membro ativo é o cadastro ativo (`inactiveSince` nulo) na data; a
 * entrada é o "Membro desde" (`memberSince`; sem ele, conta como membro desde sempre) e a
 * saída é a inativação. O público vem do registro de cada culto (`audience`), e o culto
 * sem registro é "não registrado" — nunca zero.
 *
 * O tipo de culto é a série que se repete (pelo título: "Culto da Manhã"); o evento que
 * não se repete entra em "Outros eventos".
 */
import { visitorsOf } from './audience';
import { MONTH_LONG, MONTH_SHORT, addDays, ageOf, diffDays, monthKey, normalize } from './dates';
import { CHURCH_STATUS_LABEL, compareText } from './members';
import type { ChurchStatus, Member, Registration, ScheduleEvent, ServiceAudience } from './types';

// --- Período ---------------------------------------------------------------------------

export type PeriodKind = '30d' | '90d' | '12m' | 'year' | 'custom';

export const PERIOD_OPTIONS: { value: PeriodKind; label: string }[] = [
  { value: '30d', label: '30 dias' },
  { value: '90d', label: '90 dias' },
  { value: '12m', label: '12 meses' },
  { value: 'year', label: 'Este ano' },
  { value: 'custom', label: 'Personalizado' },
];

export interface DateRange {
  from: string;
  to: string;
  /** Quantos dias, contando o primeiro e o último. */
  days: number;
}

function range(from: string, to: string): DateRange {
  return { from, to, days: diffDays(from, to) + 1 };
}

/**
 * O período escolhido, terminando hoje ("Este ano" começa em 1º de janeiro do ano de hoje). O personalizado vale com as duas datas em ordem
 * e não passa de hoje; inválido, volta aos 90 dias.
 */
export function periodRange(
  kind: PeriodKind,
  today: string,
  custom?: { from: string; to: string }
): DateRange {
  if (kind === 'custom' && custom?.from && custom.to && custom.from <= custom.to && custom.from <= today) {
    return range(custom.from, custom.to > today ? today : custom.to);
  }

  if (kind === 'year') return range(`${today.slice(0, 4)}-01-01`, today);

  const DAYS: Record<Exclude<PeriodKind, 'year'>, number> = { '30d': 30, '90d': 90, '12m': 365, custom: 90 };
  const days = DAYS[kind];

  return range(addDays(today, -(days - 1)), today);
}

/** O período anterior equivalente: a mesma quantidade de dias, logo antes. */
export function previousRange(current: DateRange): DateRange {
  const to = addDays(current.from, -1);

  return range(addDays(to, -(current.days - 1)), to);
}

const inRange = (date: string, r: DateRange) => date >= r.from && date <= r.to;

/**
 * Desde quando há dado para comparar: o evento mais antigo ou o "Membro desde" mais antigo.
 * `null` sem nenhum dos dois.
 */
export function dataStart(members: readonly Member[], events: readonly ScheduleEvent[]): string | null {
  const dates = [
    ...events.map((e) => e.date),
    ...members.flatMap((m) => (m.memberSince ? [m.memberSince] : [])),
  ];

  return dates.length ? dates.reduce((a, b) => (b < a ? b : a)) : null;
}

// --- Membros ---------------------------------------------------------------------------

/** Membro na data: já tinha entrado e ainda não tinha sido inativado. */
export function memberAt(member: Pick<Member, 'memberSince' | 'inactiveSince'>, date: string): boolean {
  return (
    (member.memberSince === null || member.memberSince <= date) &&
    (member.inactiveSince === null || member.inactiveSince > date)
  );
}

export function activeAt(members: readonly Member[], date: string): number {
  return members.filter((m) => memberAt(m, date)).length;
}

export function joinsIn(members: readonly Member[], r: DateRange): number {
  return members.filter((m) => m.memberSince !== null && inRange(m.memberSince, r)).length;
}

export function exitsIn(members: readonly Member[], r: DateRange): number {
  return members.filter((m) => m.inactiveSince !== null && inRange(m.inactiveSince, r)).length;
}

export interface MonthPoint {
  /** "2026-10". */
  key: string;
  /** "out". */
  short: string;
  /** "outubro de 2026". */
  label: string;
  /** Membros ativos no fim do mês (no mês corrente, hoje). */
  active: number;
  joins: number;
  exits: number;
}

export interface MonthSpan {
  key: string;
  short: string;
  label: string;
  first: string;
  /** O último dia do mês, ou `today` no mês corrente. */
  last: string;
}

/** Os 12 meses terminando no mês da data, do mais antigo ao mais recente. */
export function lastTwelveMonths(today: string): MonthSpan[] {
  const [year, month] = today.split('-').map(Number) as [number, number];

  return Array.from({ length: 12 }, (_, i) => {
    const offset = month - 1 - (11 - i);
    const y = year + Math.floor(offset / 12);
    const m = ((offset % 12) + 12) % 12;
    const key = `${y}-${String(m + 1).padStart(2, '0')}`;
    const next = m === 11 ? `${y + 1}-01-01` : `${y}-${String(m + 2).padStart(2, '0')}-01`;
    const end = addDays(next, -1);

    return {
      key,
      short: MONTH_SHORT[m]!,
      label: `${MONTH_LONG[m]} de ${y}`,
      first: `${key}-01`,
      last: end > today ? today : end,
    };
  });
}

/** Os últimos 12 meses, terminando no mês de hoje. */
export function monthlyEvolution(members: readonly Member[], today: string): MonthPoint[] {
  return lastTwelveMonths(today).map(({ first, last, ...month }) => {
    const r = range(first, last);

    return {
      ...month,
      active: activeAt(members, last),
      joins: joinsIn(members, r),
      exits: exitsIn(members, r),
    };
  });
}

// --- Perfil da comunidade --------------------------------------------------------------

export interface ProfileRow {
  label: string;
  /** "0–11", `''` em "Não informado". */
  hint: string;
  count: number;
}

/** Faixas etárias (os limites são o padrão; a igreja poderá ajustar). */
export const AGE_BANDS: { label: string; min: number; max: number | null }[] = [
  { label: 'Crianças', min: 0, max: 11 },
  { label: 'Adolescentes', min: 12, max: 17 },
  { label: 'Jovens', min: 18, max: 29 },
  { label: 'Adultos', min: 30, max: 59 },
  { label: 'Pessoas idosas', min: 60, max: null },
];

export const NOT_INFORMED = 'Não informado';

/** Os ativos na data por faixa etária; sem data de nascimento, "Não informado" (nunca inferido). */
export function ageProfile(members: readonly Member[], date: string): ProfileRow[] {
  const active = members.filter((m) => memberAt(m, date));
  const rows: ProfileRow[] = AGE_BANDS.map((b) => ({
    label: b.label,
    hint: b.max === null ? `${b.min}+` : `${b.min}–${b.max}`,
    count: 0,
  }));
  let unknown = 0;

  for (const m of active) {
    const age = ageOf(m.birthDate, date);
    const band =
      age === null ? -1 : AGE_BANDS.findIndex((b) => age >= b.min && (b.max === null || age <= b.max));

    if (band < 0) unknown += 1;
    else rows[band]!.count += 1;
  }

  return [...rows, { label: NOT_INFORMED, hint: '', count: unknown }];
}

/** Os ativos na data pela situação na igreja; sem situação, "Não informado". */
export function statusProfile(members: readonly Member[], date: string): ProfileRow[] {
  const active = members.filter((m) => memberAt(m, date));
  const keys = Object.keys(CHURCH_STATUS_LABEL) as ChurchStatus[];

  return [
    ...keys.map((k) => ({
      label: CHURCH_STATUS_LABEL[k],
      hint: '',
      count: active.filter((m) => m.churchStatus === k).length,
    })),
    { label: NOT_INFORMED, hint: '', count: active.filter((m) => m.churchStatus === null).length },
  ];
}

// --- Cultos e público ------------------------------------------------------------------

export const OTHER_TYPE = 'other';

export interface ServiceType {
  key: string;
  label: string;
}

/** O tipo de culto do evento: a série que se repete, pelo título; o resto é "Outros eventos". */
export function serviceTypeOf(event: Pick<ScheduleEvent, 'rule' | 'recurrence' | 'title'>): string {
  return event.rule || event.recurrence ? `t:${normalize(event.title.trim())}` : OTHER_TYPE;
}

/** Os tipos de culto dos eventos, do que mais acontece ao que menos, com "Outros eventos" no fim. */
export function serviceTypes(events: readonly ScheduleEvent[]): ServiceType[] {
  const byKey = new Map<string, { label: string; count: number }>();

  for (const e of events) {
    const key = serviceTypeOf(e);
    const seen = byKey.get(key);

    if (seen) seen.count += 1;
    else byKey.set(key, { label: key === OTHER_TYPE ? 'Outros eventos' : e.title.trim(), count: 1 });
  }

  type Entry = [string, { label: string; count: number }];
  const order = ([ka, a]: Entry, [kb, b]: Entry) => {
    if (ka === OTHER_TYPE || kb === OTHER_TYPE) return Number(ka === OTHER_TYPE) - Number(kb === OTHER_TYPE);

    return b.count - a.count || compareText(a.label, b.label);
  };

  return [...byKey.entries()].sort(order).map(([key, { label }]) => ({ key, label }));
}

/** Os cultos que já aconteceram (até hoje) no período, do mais antigo ao mais recente. */
export function servicesIn(
  events: readonly ScheduleEvent[],
  r: DateRange,
  today: string,
  type: string = 'all'
): ScheduleEvent[] {
  return events
    .filter((e) => e.date <= today && inRange(e.date, r) && (type === 'all' || serviceTypeOf(e) === type))
    .sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));
}

const recorded = (events: readonly ScheduleEvent[]) =>
  events.flatMap((e): ServiceAudience[] => (e.audience ? [e.audience] : []));

/** Média do público presencial dos cultos com registro (arredondada); `null` sem registro. */
export function averageAudience(events: readonly ScheduleEvent[]): number | null {
  const list = recorded(events);

  return list.length ? Math.round(list.reduce((sum, a) => sum + a.total, 0) / list.length) : null;
}

export interface AudienceMonth {
  key: string;
  short: string;
  label: string;
  /** Média presencial dos registrados no mês; `null` sem registro. */
  average: number | null;
  online: number | null;
  recorded: number;
  missing: number;
}

/** Os cultos agrupados por mês (para períodos longos, em que um ponto por culto não cabe). */
export function audienceByMonth(events: readonly ScheduleEvent[]): AudienceMonth[] {
  const groups = new Map<string, ScheduleEvent[]>();

  for (const e of events) {
    const key = monthKey(e.date);
    groups.set(key, [...(groups.get(key) ?? []), e]);
  }

  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, list]) => {
      const [y, m] = key.split('-').map(Number) as [number, number];
      const withOnline = recorded(list).filter((a) => a.online !== null);

      return {
        key,
        short: MONTH_SHORT[m - 1]!,
        label: `${MONTH_LONG[m - 1]} de ${y}`,
        average: averageAudience(list),
        online: withOnline.length
          ? Math.round(withOnline.reduce((s, a) => s + a.online!, 0) / withOnline.length)
          : null,
        recorded: recorded(list).length,
        missing: list.filter((e) => !e.audience).length,
      };
    });
}

// --- Indicadores -----------------------------------------------------------------------

export interface MembershipNumbers {
  active: number;
  joins: number;
  exits: number;
  net: number;
  services: number;
  recorded: number;
  missing: number;
  averageAudience: number | null;
  /** Visitantes de primeira vez (soma dos registros que informaram); `null` sem nenhum. */
  firstTime: number | null;
  recurring: number | null;
}

function sumOf(list: ServiceAudience[], key: 'firstTime' | 'recurring'): number | null {
  const informed = list.filter((a) => a[key] !== null);

  return informed.length ? informed.reduce((s, a) => s + a[key]!, 0) : null;
}

export function membershipNumbers(
  members: readonly Member[],
  events: readonly ScheduleEvent[],
  r: DateRange,
  today: string
): MembershipNumbers {
  const services = servicesIn(events, r, today);
  const list = recorded(services);
  const joins = joinsIn(members, r);
  const exits = exitsIn(members, r);

  return {
    active: activeAt(members, r.to),
    joins,
    exits,
    net: joins - exits,
    services: services.length,
    recorded: list.length,
    missing: services.length - list.length,
    averageAudience: averageAudience(services),
    firstTime: sumOf(list, 'firstTime'),
    recurring: sumOf(list, 'recurring'),
  };
}

/** Variação em pontos (contagem) ou em % (média); `null` quando não há base para comparar. */
export function delta(current: number | null, previous: number | null, percent = false): number | null {
  if (current === null || previous === null) return null;
  if (!percent) return current - previous;
  if (previous === 0) return null;

  return Math.round(((current - previous) / previous) * 100);
}

// --- Pendências e destaques ------------------------------------------------------------

/** `visitors` vem de `visitorInsights` (`visitors.ts`), só para quem gerencia visitantes. */
export type InsightKind = 'registrations' | 'unrecorded' | 'trend' | 'visitors';

export interface Insight {
  kind: InsightKind;
  text: string;
  /** No destaque de tendência, o tipo de culto; nos visitantes, `overdue` ou `unassigned`. */
  type?: string;
  count?: number;
}

/** Variação mínima da média de um tipo de culto para virar destaque, e a amostra mínima. */
export const TREND_MIN_PERCENT = 5;
export const TREND_MIN_SAMPLE = 3;

/**
 * Calculados dos dados: cadastros aguardando análise, cultos dos últimos 30 dias (antes
 * de hoje) sem público registrado e, com a comparação ligada, mudança de 5% ou mais na
 * média de cada tipo de culto — descritiva, sem atribuir causa.
 */
export function membershipInsights(input: {
  events: readonly ScheduleEvent[];
  registrations: readonly Registration[];
  range: DateRange;
  previous: DateRange | null;
  today: string;
}): Insight[] {
  const { events, registrations, range: r, previous, today } = input;
  const out: Insight[] = [];
  const pending = registrations.filter((x) => x.status === 'pending').length;

  if (pending) {
    out.push({
      kind: 'registrations',
      count: pending,
      text: `Há ${pending} ${pending > 1 ? 'solicitações' : 'solicitação'} de cadastro aguardando análise`,
    });
  }

  const unrecorded = unrecordedRecently(events, today).length;

  if (unrecorded) {
    out.push({
      kind: 'unrecorded',
      count: unrecorded,
      text: `O público de ${unrecorded} ${unrecorded > 1 ? 'eventos' : 'evento'} dos últimos 30 dias ainda não foi registrado`,
    });
  }

  if (previous) {
    for (const type of serviceTypes(events)) {
      const now = recorded(servicesIn(events, r, today, type.key));
      const before = recorded(servicesIn(events, previous, today, type.key));

      if (now.length < TREND_MIN_SAMPLE || before.length < TREND_MIN_SAMPLE) continue;

      const a1 = averageAudience(servicesIn(events, r, today, type.key))!;
      const a0 = averageAudience(servicesIn(events, previous, today, type.key))!;
      const p = delta(a1, a0, true);

      if (p === null || Math.abs(p) < TREND_MIN_PERCENT) continue;

      const name = type.key === OTHER_TYPE ? 'dos outros eventos' : `do ${type.label}`;

      out.push({
        kind: 'trend',
        type: type.key,
        text: `A média de público ${name} ${p > 0 ? 'aumentou' : 'diminuiu'} ${Math.abs(p)}% em relação ao período anterior (${a0.toLocaleString('pt-BR')} → ${a1.toLocaleString('pt-BR')})`,
      });
    }
  }

  return out;
}

/** Cultos dos últimos 30 dias, já acontecidos (antes de hoje), sem público registrado. */
export function unrecordedRecently(events: readonly ScheduleEvent[], today: string): ScheduleEvent[] {
  return events.filter((e) => !e.audience && e.date < today && e.date >= addDays(today, -30));
}

/**
 * O culto que o "Registrar público do culto" abre: o mais recente sem registro (até hoje)
 * ou, com todos registrados, o mais recente.
 */
export function nextAudienceToRecord(events: readonly ScheduleEvent[], today: string): ScheduleEvent | null {
  const past = events
    .filter((e) => e.date <= today)
    .sort((a, b) => (b.date + b.time).localeCompare(a.date + a.time));

  return past.find((e) => !e.audience) ?? past[0] ?? null;
}

/** Visitantes do culto em texto: "12 · 4 de primeira vez e 8 de retorno"; `''` sem dado. */
export function visitorsText(audience: ServiceAudience): string {
  const total = visitorsOf(audience);

  if (total === null) return '';

  return `${total.toLocaleString('pt-BR')} · ${(audience.firstTime ?? 0).toLocaleString('pt-BR')} de primeira vez e ${(audience.recurring ?? 0).toLocaleString('pt-BR')} de retorno`;
}
